import {Injectable} from '@angular/core';
import {Observable, of, forkJoin} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {RegionService} from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import {ServerTranslateService} from "@services/server-translate.service";
import {TranslateService} from "@ngx-translate/core";

export interface RiskData {
    region: string;
    totalArea: number;
    crops: CropRiskData[];
}

export interface CropRiskData {
    name: string;
    riskLevel: 'low' | 'medium' | 'high';
    riskPercent: number;
    affectedArea: number;
    climateImpact: string;
}

@Injectable({
    providedIn: 'root'
})
export class RiskDataService {
    private climateImpacts = [
        'High temperatures, dry winds',
        'Hail, heavy rain',
        'Frosts',
        'Заморозки',
        'Pests and diseases',
        'Excessive humidity'
    ];

    constructor(
        private bidService: BidService,
        private regionService: RegionService,
        private cropService: CropService,
        private serverTranslateService: ServerTranslateService,
        private translateService: TranslateService,

    ) {
    }

    getRiskDataForRegion(regionName: string, regionId?: string): Observable<RiskData> {
        const bidQuery: any = {
            take: 1000,
            offset: 0
        };
        if (regionId) {
            bidQuery.filters = [{
                filterField: 'regionId',
                filterValue: regionId,
                filterOperator: 0 // Equals operator
            }];
        }

        return forkJoin({
            bids: this.bidService.cropMonitoringBidGetByQueryPost(bidQuery).pipe(
                catchError(() => of([]))
            ),
            regions: this.regionService.dictionariesRegionGetByQueryPost({
                take: 1000,
                offset: 0
            }).pipe(
                catchError(() => of([]))
            ),
            crops: this.cropService.dictionariesCropGetByQueryPost({
                take: 1000,
                offset: 0
            }).pipe(
                catchError(() => of([]))
            )
        }).pipe(
            map(({bids, regions, crops}) => {
                const bidsArray = bids || [];
                const regionsArray = regions || [];
                const cropsArray = crops || [];

                let regionBids = bidsArray;

                if (!regionId) {
                    const region = regionsArray.find((r: any) =>
                        r.nameEn === regionName ||
                        r.nameRu === regionName ||
                        r.nameUz === regionName
                    );
                    if (!region) {
                        return this.generateMockRiskData(regionName);
                    }
                    regionBids = bidsArray.filter((bid: any) => bid.regionId === region.id);
                }

                if (regionBids.length === 0) {
                    return this.generateMockRiskData(regionName);
                }


                 const cropRisks = new Map<string, any>();
                 let totalArea = regionBids.length; // Number of bids

                 regionBids.forEach((bid: any) => {
                     if (!cropRisks.has(bid.cropId)) {
                         cropRisks.set(bid.cropId, {
                             totalBids: 0,
                             totalArea: 0,
                             climateInfluence: 0
                         });
                     }

                     const risk = cropRisks.get(bid.cropId);
                     risk.totalBids += 1;
                     risk.totalArea += 1; // Each bid is counted as 1 unit of area
                });

                const cropRiskData: CropRiskData[] = [];

                cropRisks.forEach((risk, cropId) => {
                    const crop = cropsArray.find((c: any) => c.id === cropId);

                    const riskPercent = risk.totalArea > 0 ? (risk.totalBids / totalArea) * 100 : 0;
                    const serverTranslateService = this.serverTranslateService;
                    const getClimateImpact = this.getClimateImpact.bind(this);
                    const  translateService = this.translateService;

                    cropRiskData.push({
                        get name(): string {
                            return serverTranslateService.translate(crop, 'name') as string;
                        },
                        riskLevel: this.getRiskLevel(riskPercent),
                        riskPercent: parseFloat(riskPercent.toFixed(1)),
                        affectedArea: parseFloat(risk.totalArea.toFixed(2)),
                        get climateImpact(): string {
                            return translateService.instant(getClimateImpact(risk.climateInfluence))
                        }
                    });
                });

                cropRiskData.sort((a, b) => b.riskPercent - a.riskPercent);

                return {
                    region: regionName,
                    totalArea: parseFloat(totalArea.toFixed(2)),
                    crops: cropRiskData
                };
            }),
            catchError(() => of(this.generateMockRiskData(regionName)))
        );
    }

    private getRiskLevel(riskPercent: number): 'low' | 'medium' | 'high' {
        if (riskPercent < 20) return 'low';
        if (riskPercent < 40) return 'medium';
        return 'high';
    }

    private getClimateImpact(climateInfluence: number): string {
        const index = Math.min(Math.floor(climateInfluence), this.climateImpacts.length - 1);
        return this.climateImpacts[Math.max(0, index)];
    }

    private generateMockRiskData(regionName: string): RiskData {
        const crops = [
            {name: 'Пшеница', baseRisk: 15},
            {name: 'Хлопок', baseRisk: 25},
            {name: 'Кукуруза', baseRisk: 35},
            {name: 'Рис', baseRisk: 20},
            {name: 'Виноград', baseRisk: 30}
        ];

        const selectedCrops = crops
            .sort(() => Math.random() - 0.5)
            .slice(0, Math.floor(Math.random() * 3) + 2);

        const cropRiskData = selectedCrops.map(crop => {
            const riskVariation = (Math.random() - 0.5) * 20;
            const riskPercent = Math.max(5, Math.min(65, crop.baseRisk + riskVariation));

            return {
                name: crop.name,
                riskLevel: this.getRiskLevel(riskPercent),
                riskPercent: parseFloat(riskPercent.toFixed(1)),
                affectedArea: parseFloat((Math.random() * 3000 + 500).toFixed(2)),
                climateImpact: this.climateImpacts[Math.floor(Math.random() * this.climateImpacts.length)]
            };
        });

        cropRiskData.sort((a, b) => b.riskPercent - a.riskPercent);

        return {
            region: regionName,
            totalArea: parseFloat((Math.random() * 10000 + 1000).toFixed(2)),
            crops: cropRiskData
        };
    }
}
